/**
 * Purpose: Custom hook for loading messages for a chat
 *
 * How it works:
 * 1. Sets up an AbortController to handle cancellation
 * 2. Progressively loads messages for the active chat
 * 3. Handles error cases with toast notifications
 * 4. Cleans up by aborting requests when unmounting
 *
 * Key features:
 * - Automatic cancellation on unmount or chat change
 * - Error handling with user-friendly notifications
 * - Proper signal handling for API requests
 */

import { useEffect } from "react";
// import { progressivelyLoadMessages } from "@/middleware/messages";
import { useToast } from "@/hooks/useToast";
import { useTranslation } from "react-i18next";
import { umbra_progressivelyLoadMessages } from "@/middleware/umbra_messages";

/**
 * Hook to load messages for a specific chat
 * @param activeChatId | null
 */
export function useMessageLoader(activeChatId: string | null) {
  const { toast } = useToast();
  const { t } = useTranslation();

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    const msgInit = async () => {
      try {
        if (!activeChatId) {
          return;
        }

        // await progressivelyLoadMessages(activeChatId, 1000, signal);
        await umbra_progressivelyLoadMessages(activeChatId, 2000, signal);
      } catch (error) {
        if (
          error instanceof Error &&
          error.message &&
          error.message === "canceled"
        ) {
          return;
        }
        toast({
          duration: 1000,
          description:
            error instanceof Error ? error.message : t("errorOccurred"),
          variant: "destructive",
        });
      }
    };

    msgInit();

    return () => {
      // Cancel the request when the component unmounts
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChatId, toast]);
}
